import * as React from 'react';
import { Modal } from 'react-bootstrap';
import { Entity, isBlank, QueryOptions, ReferenceField } from 'gossamer-unified';
import { FormControl } from './FormControl';
import { UneditableReference } from './UneditableReference';
import styles from './RecordPicker.module.css';
import { PaginatorAndListSettings } from '../sections/Paginator';
import { useCacheQuery } from '../data/useCacheQuery';
import { useQueryOptionsChanges } from '../data/useQueryOptions';
import { Search } from '../sections/Search';

interface RecordPickerProps {
  altText?: string; // to be shown if row is not supplied
  className?: string;
  clearOnSelection?: boolean;
  editable: boolean;
  field: ReferenceField;
  modalTitle?: string;
  onItemSelected: (newValue: string | null) => void;
  queryOptions?: QueryOptions;
  suppressLink?: boolean;
  validity?: boolean;
  value: string | number | null;
}

export const RecordPicker: React.FC<RecordPickerProps> = (props) => {
  const [showModal, setShowModal] = React.useState(false);
  const hasValue = !isBlank(props.value);
  const hasAltText = !isBlank(props.altText);
  const onRowClick = (key: string) => {
    setShowModal(false);
    props.onItemSelected(key);
  };
  const queryOptions: QueryOptions = props.queryOptions || getRecordPickerQueryOptions(props.field);
  return (
    <FormControl className={props.className} flexDirection="row" validity={props.validity}>
      <button type="button" className="ControlIcon valueSelector" onClick={() => setShowModal(true)}>
        🔍
      </button>
      {hasAltText && <div className={styles.RecordPickerLabel}>{props.altText}</div>}
      {hasValue && !hasAltText && (
        <div className={styles.ReferenceLabel}>
          <UneditableReference refEntity={props.field.refEntity} suppressLink={true} value={props.value} />
        </div>
      )}
      {hasValue && (
        <button onClick={() => props.onItemSelected(null)} className="ControlIcon valueClearer" type="button">
          ✖
        </button>
      )}
      {showModal && (
        <RecordPickerModal
          field={props.field}
          modalTitle={props.modalTitle}
          onHide={() => setShowModal(false)}
          onRowClick={onRowClick}
          queryOptions={queryOptions}
        />
      )}
    </FormControl>
  );
};

interface RecordPickerModalProps {
  field: ReferenceField;
  modalTitle?: string;
  onHide: () => void;
  onRowClick: (key: string) => void;
  queryOptions: QueryOptions;
}

export const RecordPickerModal = (props: RecordPickerModalProps): React.JSX.Element => (
  <Modal
    show={true}
    onHide={props.onHide}
    aria-labelledby="ref-value-picker-modal-title"
    size="xl"
    fullscreen="xl-down"
  >
    <Modal.Header closeButton>
      <Modal.Title id="ref-value-picker-modal-title">
        {props.modalTitle || `Select a ${props.field.refEntity.title}`}
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <SearchQuery entity={props.field.refEntity} onRowClick={props.onRowClick} queryOptions={props.queryOptions} />
    </Modal.Body>
  </Modal>
);

// the default query for the records offered by a reference field's picker
export const getRecordPickerQueryOptions = (field: ReferenceField): QueryOptions =>
  field.queryOptions || {
    where: {
      and: field.refFilters || field.refEntity.defaultFilters || [],
    },
    order: field.refEntity.defaultSort,
  };

interface SearchQueryProps {
  entity: Entity<any>;
  onRowClick: (key: string) => void;
  queryOptions: QueryOptions;
}

const SearchQuery = (props: SearchQueryProps) => {
  const [queryOptions, setQueryOptions] = useQueryOptionsChanges(props.entity, props.queryOptions);
  const gCacheQueryItem = useCacheQuery(props.entity, queryOptions);
  return (
    <PaginatorAndListSettings
      entity={props.entity}
      gCacheQuery={gCacheQueryItem}
      onRowClick={props.onRowClick}
      queryOptions={queryOptions}
      render={Search}
      setQueryOptions={setQueryOptions}
    />
  );
};
